import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useTranslation } from "react-i18next";
import BookingGetters from "../../store/onlineBooking/getters";

type Props = {
  title: string;
  progress: number;
  children: React.ReactNode;
  onBack: () => void;
  wide?: boolean;
  isLoading?: boolean;
};

const WebCheckInLayout = ({
  title,
  progress,
  children,
  onBack,
  wide,
  isLoading,
}: Props) => {
  const { t: translate } = useTranslation();
  const translatedTitle = translate(title);
  const hideBackButton =
    translatedTitle === translate("WEB CHECK IN") ||
    translatedTitle === translate("SELECT YOUR PROFILE") ||
    translatedTitle === translate("WEB CHECK IN COMPLETED");

  const appSettings = BookingGetters.getAppSettings();
  const webCheckinHeaderBgColor = appSettings?.webCheckinHeaderBgColor;
  const webCheckinHeaderTextColor = appSettings?.webCheckinHeaderTextColor;
  const webCheckinContentBgColor = appSettings?.webCheckinContentBgColor;
  const webCheckinContentTextColor = appSettings?.webCheckinContentTextColor;
  const webCheckinButtonColor = appSettings?.webCheckinButtonColor;
  const webCheckinButtonTextColor = appSettings?.webCheckinButtonTextColor;
  const webCheckinProgressBarColor = appSettings?.webCheckinProgressBarColor;

  const hideLinearProgress =
    translatedTitle === translate("WEB CHECK IN") ||
    translatedTitle === translate("Reservation Number") ||
    translatedTitle === translate("Voucher Number") ||
    translatedTitle === translate("Reservation Details");

  const webCheckInLinearProgress = {
    height: "10px",
    backgroundColor: "#E1E1E1",
    "& .MuiLinearProgress-bar": {
      backgroundColor: webCheckinProgressBarColor
        ? webCheckinProgressBarColor
        : "#19344FCC",
    },
  };

  return (
    <Card
      className={wide ? "webCheckInCardWide" : "webCheckInCard"}
      sx={{
        backgroundColor: webCheckinContentBgColor || "#F4F4F4",
      }}
    >
      {/* Header */}
      <Box
        className="webCheckInBox"
        sx={{
          backgroundColor: webCheckinHeaderBgColor || "#2cb133",
        }}
      >
        {!hideBackButton && (
          <IconButton
            onClick={onBack}
            className="webCheckInIconButton"
            sx={{
              color: webCheckinHeaderTextColor || "#fff",
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}
        <Typography
          variant="h6"
          className="webCheckInHeader"
          sx={{
            color: webCheckinHeaderTextColor || "#fff",
          }}
        >
          {translate(title)}
        </Typography>
      </Box>

      {/* Progress Bar */}
      {!hideLinearProgress && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={webCheckInLinearProgress}
        />
      )}
      {/* Content */}
      <Box sx={{ position: "relative" }}>
        <CardContent
          sx={{
            padding: "30px",
            "& .webCheckInButton": {
              backgroundColor: webCheckinButtonColor || "#19344F",
              color: webCheckinButtonTextColor || "#FFFFFF",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: webCheckinButtonColor || "#19344F",
                color: webCheckinButtonTextColor || "#FFFFFF",
              },
            },
            "& .webCheckInText": {
              color: webCheckinContentTextColor || "#19344FCC",
            },
          }}
          className="webCheckInCardContent"
        >
          {children}
        </CardContent>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "12px",
            }}
          >
            <CircularProgress
              size={50}
              thickness={3}
              sx={{ color: webCheckinHeaderBgColor || "#2cb133" }}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default WebCheckInLayout;
